/**
 * Dialog — the reusable modal primitive (E10-F02-T01).
 *
 * The legacy front-end (index.html) declares a couple dozen native `<dialog>`
 * elements (unsaved, link-email, reply-email, email-preview, doc-preview,
 * regenerate, doc-edit, stage5-send, data-detail, add-company, bulk-add,
 * publish, user, prompt, staff). They all share ONE visual pattern:
 *
 *   - a navy title bar (`bg-primary text-white`) with the heading on the left
 *     and a close cross (`fa-times`) on the right;
 *   - a white body (`rounded-lg shadow-xl`, configurable max-width);
 *   - an optional bordered footer with right-aligned actions
 *     (outline Cancel + a primary / accent action);
 *   - a dimming scrim backdrop (`bg-gray-900/50`).
 *
 * A handful of dialogs (notably `unsaved-dialog`) drop the navy header and are
 * just a plain white card — that is the headerless variant (`title` omitted).
 *
 * Behaviour parity with the native `<dialog>.showModal()`:
 *   - rendered only while `open` (mount/unmount, like show/close);
 *   - Esc closes (calls `onClose`) unless suppressed;
 *   - a click on the scrim closes (the native light-dismiss), unless suppressed;
 *   - focus is trapped inside the dialog while open and restored to the
 *     previously-focused element on close;
 *   - `aria-modal` + `role="dialog"` + a labelled heading for a11y.
 *
 * The focus-trap / return-focus / Esc / scrim mechanics live in the shared
 * `modal-layer` hook (E10-F02-T06) so the centred Dialog and the slide-over
 * Drawer cannot drift apart on the a11y contract.
 *
 * This is a presentational primitive: it owns chrome + focus/keyboard mechanics
 * only. Feature dialogs compose it and pass their body + footer actions; the
 * unsaved-changes guard, send-email form, etc. are separate tasks that render
 * INTO this shell.
 *
 * Colours go through the brand token utilities (`bg-primary`, `text-accent`, …)
 * mapped in styles/index.css @theme — never literal hexes (design.md "Don't").
 */
import { useId, type ReactNode } from 'react';
import { ModalPortal, useModalLayer, useScrimDismiss } from './modal-layer.js';

/** Configurable body max-width. Maps to a Tailwind `max-w-*` utility. */
export type DialogMaxWidth =
  | 'sm'
  | 'md'
  | 'lg'
  | 'xl'
  | '2xl'
  | '3xl'
  | '4xl'
  | '5xl'
  | '6xl'
  | '7xl';

const MAX_WIDTH_CLASS: Record<DialogMaxWidth, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  // Wide work surfaces (E05-F09 "Test prompt": draft + rendered prompt side by
  // side, then the result tabs). Still capped by the backdrop's p-4, so on a
  // narrow screen these degrade to the viewport width like every other size.
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
  '7xl': 'max-w-7xl',
};

export interface DialogProps {
  /** Whether the dialog is shown. When false the dialog is not rendered at all. */
  readonly open: boolean;
  /**
   * Close request — fired by Esc, the close cross, or a scrim click. The parent
   * owns `open`, so this is where it flips it to false (and may run guards, e.g.
   * the unsaved-changes flow can intercept and keep the dialog open).
   */
  readonly onClose: () => void;
  /**
   * Heading text. When provided the navy title bar (with the close cross) is
   * rendered; when omitted the dialog is a plain white card (the headerless
   * `unsaved-dialog` variant) and the body is responsible for its own actions.
   */
  readonly title?: ReactNode;
  /** Dialog body content. */
  readonly children: ReactNode;
  /**
   * Optional footer node — rendered in a top-bordered strip with right-aligned
   * content. Compose the outline Cancel + primary/accent action here (or use
   * the {@link DialogFooter} helper). Omit for dialogs without a footer.
   */
  readonly footer?: ReactNode;
  /** Body max-width. Defaults to `lg` (parity with the common send/edit dialogs). */
  readonly maxWidth?: DialogMaxWidth;
  /** Disable Esc-to-close. Default false (Esc closes). */
  readonly disableEscapeClose?: boolean;
  /** Disable scrim-click-to-close (light dismiss). Default false (click closes). */
  readonly disableBackdropClose?: boolean;
  /** Test id on the scrim/root (also used to derive child test ids). */
  readonly testId?: string;
  /** Extra classes appended to the dialog panel. */
  readonly className?: string;
  /** Accessible label when there is no visible `title` (headerless variant). */
  readonly ariaLabel?: string;
}

/**
 * Right-aligned footer helper: an outline Cancel + a primary/accent action,
 * matching the legacy bordered footer. Use it directly as the `footer` prop, or
 * pass your own node for bespoke footers.
 */
export interface DialogFooterProps {
  /** Cancel handler. Usually the dialog's `onClose`. */
  readonly onCancel: () => void;
  /** Primary action handler. */
  readonly onConfirm: () => void;
  /** Cancel button label. Default "Cancel". */
  readonly cancelLabel?: string;
  /** Primary action label. Default "Save". */
  readonly confirmLabel?: string;
  /**
   * Visual weight of the primary action:
   *   - `primary` → navy fill with gold text (the brand primary button);
   *   - `accent`  → gold fill with navy text;
   *   - `danger`  → red fill (destructive confirmations).
   * Default `primary`.
   */
  readonly variant?: 'primary' | 'accent' | 'danger';
  /** Disable the primary action (e.g. invalid form). */
  readonly confirmDisabled?: boolean;
  /** Test id prefix; buttons get `<testId>-cancel` / `<testId>-confirm`. */
  readonly testId?: string;
}

const CONFIRM_VARIANT_CLASS: Record<NonNullable<DialogFooterProps['variant']>, string> = {
  primary: 'bg-primary text-surface hover:bg-primary-hover',
  accent: 'bg-accent text-primary hover:bg-accent-hover',
  danger: 'bg-danger text-white hover:opacity-90',
};

export function DialogFooter({
  onCancel,
  onConfirm,
  cancelLabel = 'Cancel',
  confirmLabel = 'Save',
  variant = 'primary',
  confirmDisabled = false,
  testId = 'dialog-footer',
}: DialogFooterProps): React.JSX.Element {
  return (
    <>
      <button
        type="button"
        data-testid={`${testId}-cancel`}
        onClick={onCancel}
        className="rounded border border-border bg-white px-4 py-2 text-sm font-medium text-text-strong hover:bg-gray-50"
      >
        {cancelLabel}
      </button>
      <button
        type="button"
        data-testid={`${testId}-confirm`}
        onClick={onConfirm}
        disabled={confirmDisabled}
        className={`rounded px-4 py-2 text-sm font-semibold disabled:opacity-60 ${CONFIRM_VARIANT_CLASS[variant]}`}
      >
        {confirmLabel}
      </button>
    </>
  );
}

export function Dialog({
  open,
  onClose,
  title,
  children,
  footer,
  maxWidth = 'lg',
  disableEscapeClose = false,
  disableBackdropClose = false,
  testId = 'dialog',
  className,
  ariaLabel,
}: DialogProps): React.JSX.Element | null {
  const titleId = useId();
  const hasHeader = title !== undefined && title !== null;

  // Focus-trap + return-focus + Esc + background-block — shared with Drawer.
  const { panelRef, handleKeyDown } = useModalLayer<HTMLDivElement>({
    open,
    onClose,
    disableEscapeClose,
  });

  // Scrim light-dismiss (shared factory): close only when the press both starts
  // AND lands on the scrim itself.
  const handleBackdropMouseDown = useScrimDismiss<HTMLDivElement>(onClose, disableBackdropClose);

  if (!open) return null;

  // Portalled to <body> so the background-block can inert the real page root
  // (#root) rather than #root's non-existent app-content siblings — see
  // modal-layer.ts.
  return (
    <ModalPortal>
      <div
        data-testid={`${testId}-backdrop`}
        onMouseDown={handleBackdropMouseDown}
        className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4"
      >
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={hasHeader ? titleId : undefined}
          aria-label={hasHeader ? undefined : ariaLabel}
          data-testid={testId}
          tabIndex={-1}
          onKeyDown={handleKeyDown}
          className={`flex max-h-[90vh] w-full ${MAX_WIDTH_CLASS[maxWidth]} flex-col overflow-hidden rounded-lg bg-white shadow-xl focus:outline-none ${className ?? ''}`}
        >
          {hasHeader ? (
            <header className="flex items-center justify-between bg-primary px-6 py-4 text-white">
              <h2
                id={titleId}
                data-testid={`${testId}-title`}
                className="brand-font text-lg font-semibold text-white"
              >
                {title}
              </h2>
              <button
                type="button"
                data-testid={`${testId}-close`}
                aria-label="Close"
                onClick={onClose}
                className="-mr-1 flex h-8 w-8 items-center justify-center rounded text-white/80 hover:bg-primary-hover hover:text-white"
              >
                <i className="fa-solid fa-times" aria-hidden />
              </button>
            </header>
          ) : null}

          <div data-testid={`${testId}-body`} className="overflow-y-auto px-6 py-5 text-text">
            {children}
          </div>

          {footer ? (
            <footer
              data-testid={`${testId}-footer`}
              className="flex items-center justify-end gap-3 border-t border-border px-6 py-4"
            >
              {footer}
            </footer>
          ) : null}
        </div>
      </div>
    </ModalPortal>
  );
}
